<script lang="ts">
/**
 * Pre6 Apps against Pre6 Enterprise on one hard job: a customer ECN on a safety-critical part
 * (steering knuckle SK-2207, rev C to rev D, the Ø42 bore tightened from H7 to H6 from lot 2611, impact
 * due Friday). Seven rows, one difference each (how it starts and where it lands, what it knows, who
 * does the work, what you get, what it can do, where it runs, how you get it), and every answer shown
 * as a small piece of that job rather than a phrase. The apps column stays grey and plain; the
 * Enterprise column sits on one continuous panel. Rows rise in as they come into view, and the pieces
 * inside each Enterprise cell land one after another.
 */
import Calculator from "lucide-svelte/icons/calculator";
import Check from "lucide-svelte/icons/check";
import Cloud from "lucide-svelte/icons/cloud";
import Cog from "lucide-svelte/icons/cog";
import Crosshair from "lucide-svelte/icons/crosshair";
import ClipboardCheck from "lucide-svelte/icons/clipboard-check";
import FileDiff from "lucide-svelte/icons/file-diff";
import FileText from "lucide-svelte/icons/file-text";
import ListChecks from "lucide-svelte/icons/list-checks";
import Gauge from "lucide-svelte/icons/gauge";
import Handshake from "lucide-svelte/icons/handshake";
import Library from "lucide-svelte/icons/library";
import Lock from "lucide-svelte/icons/lock";
import Mail from "lucide-svelte/icons/mail";
import Network from "lucide-svelte/icons/network";
import PackageCheck from "lucide-svelte/icons/package-check";
import Plus from "lucide-svelte/icons/plus";
import Rocket from "lucide-svelte/icons/rocket";
import Server from "lucide-svelte/icons/server";
import ShieldCheck from "lucide-svelte/icons/shield-check";
import Sparkles from "lucide-svelte/icons/sparkles";
import Users from "lucide-svelte/icons/users";
import X from "lucide-svelte/icons/x";
import Zap from "lucide-svelte/icons/zap";
import { onMount } from "svelte";

const A = {
	pre6: { name: "Pre6 Agent", color: "var(--agent-pre6)", icon: Network },
	rfq: { name: "RFQ Agent", color: "var(--agent-rfq)", icon: Calculator },
	prod: { name: "Production Agent", color: "var(--agent-prod)", icon: Cog },
	qa: { name: "Quality Agent", color: "var(--agent-qa)", icon: Crosshair },
};

const CREW = [
	{ ...A.pre6, task: "Runs the ECN and checks the others' work" },
	{ ...A.qa, task: "Marks the change on the drawing" },
	{ ...A.prod, task: "Sorts open orders and updates routings" },
	{ ...A.rfq, task: "Reprices lots from 2611" },
];

/** The crew deployed on the ECN: one agent per piece of the job, end to end. */
const SWARM = [
	{ task: "Diff rev C and D", color: A.qa.color, icon: FileDiff },
	{ task: "Sort open orders", color: A.prod.color, icon: ListChecks },
	{ task: "Add hone op", color: A.prod.color, icon: Cog },
	{ task: "Reprice lots", color: A.rfq.color, icon: Calculator },
	{ task: "Draft control plan", color: A.qa.color, icon: ClipboardCheck },
	{ task: "Update PFMEA", color: A.qa.color, icon: ShieldCheck },
	{ task: "Reply to the OEM", color: A.pre6.color, icon: Mail },
];

const DELIVERABLES = ["Reply to the OEM", "Impact report", "2 routing changes", "Control plan", "PFMEA update"];

const TOOLS = [
	{ name: "Inbox", color: A.pre6.color },
	{ name: "PLM", color: A.qa.color },
	{ name: "ERP", color: A.prod.color },
	{ name: "QMS", color: A.qa.color },
	{ name: "Team chat", color: A.pre6.color },
];

const ROWS = [
	{ id: "flow", k: "How the work moves", icon: Zap },
	{ id: "knows", k: "What it knows", icon: Library },
	{ id: "who", k: "Who does the work", icon: Users },
	{ id: "get", k: "What you get", icon: PackageCheck },
	{ id: "pace", k: "How fast, how sure", icon: Gauge },
	{ id: "can", k: "What it can do", icon: Sparkles },
	{ id: "runs", k: "Where it runs", icon: Server },
	{ id: "how", k: "How you get it", icon: Handshake },
];

let root: HTMLDivElement;

onMount(() => {
	const io = new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				if (!e.isIntersecting) continue;
				e.target.setAttribute("data-seen", "");
				io.unobserve(e.target);
			}
		},
		{ threshold: 0.35 },
	);
	for (const el of root.querySelectorAll(".job, .row")) io.observe(el);
	return () => io.disconnect();
});
</script>

{#snippet dot(a: { color: string; icon: typeof Mail }, size = 20)}
	<span class="dot" style:--c={a.color} style:--s="{size}px"><a.icon size={Math.round(size * 0.55)} strokeWidth={2.5} /></span>
{/snippet}

<div class="cmp" bind:this={root}>
	<!-- The job both columns are answering. -->
	<div class="job">
		<span class="job-k">The job</span>
		<div class="job-mail">
			<span class="job-ic"><Mail size={15} strokeWidth={2.25} /></span>
			<p>
				<b>ECN-7734 from the OEM.</b> Steering knuckle SK-2207 goes to rev D. The Ø42 bore tightens from H7 to H6 from
				lot 2611. Cost and timing impact due Friday.
			</p>
		</div>
	</div>

	<div class="grid" role="table" aria-label="Pre6 Apps compared with Pre6 Enterprise on one ECN">
		<div class="row head" role="row">
			<span class="k" role="columnheader"><span class="sr-only">Compared on</span></span>
			<span class="apps" role="columnheader">
				<span class="speed-tag"
					><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
						><path d="M6 9h14M6 14h14M8 9v5M13 9v5M18 9v5M2 11.5h4M2 10v3M20 11.5h2.5M22.5 9.5v4" /></svg
					>First flight</span
				>
				<b>Pre6 Apps</b>
				<small>One task at a time, in the app</small>
			</span>
			<span class="ent" role="columnheader">
				<span class="crew" aria-hidden="true">{#each Object.values(A) as a (a.name)}{@render dot(a, 24)}{/each}<span class="dot more" style:--s="24px" title="And any agent you need"><Plus size={13} strokeWidth={2.75} /></span></span>
				<span class="speed-tag space"><Rocket size={13} strokeWidth={2.25} />Space travel</span>
				<b>Pre6 Enterprise<span class="stop">.</span></b>
				<small>A crew of agents, in your organization</small>
			</span>
		</div>

		{#each ROWS as r, i (r.k)}
			<div class="row" role="row" style:--i={i}>
				<span class="k" role="rowheader"><span class="k-ic"><r.icon size={15} strokeWidth={2.25} /></span>{r.k}</span>

				<!-- Pre6 Apps -->
				<div class="apps" role="cell">
					<span class="m">Pre6 Apps</span>
					{#if r.id === "flow"}
						<div class="v chain">
							<span class="node"><Mail size={12} strokeWidth={2.25} />Inbox</span>
							<span class="step"><span class="to"></span><span class="node you-n">You</span></span>
							<span class="step"><span class="to"></span><span class="apps-set"><span class="node">Pre6 Lens</span><span class="node">Pre6 Costing</span><span class="node">Pre6 PPAP</span></span></span>
							<span class="step"><span class="to"></span><span class="node you-n">You</span></span>
							<span class="step"><span class="to"></span><span class="targets"><span>Inbox</span><span>ERP</span><span>QMS</span></span></span>
						</div>
						<p>Someone carries the ECN into each app, then carries the answers back out to every system.</p>
					{:else if r.id === "knows"}
						<div class="v">
							<span class="file"><FileText size={12} strokeWidth={2.25} />SK-2207_RevC.pdf</span>
							<span class="file"><FileText size={12} strokeWidth={2.25} />SK-2207_RevD.pdf</span>
						</div>
						<p>Only the files you upload.</p>
					{:else if r.id === "who"}
						<div class="v one">
							<span class="dot grey" style:--s="22px"><Sparkles size={12} strokeWidth={2.5} /></span>
							<span>One app, one task at a time</span>
						</div>
						<p>Your team splits up the rest.</p>
					{:else if r.id === "get"}
						<div class="v">
							<span class="file"><FileText size={12} strokeWidth={2.25} />Control plan<em>Pre6 PPAP</em></span>
							<span class="file"><FileText size={12} strokeWidth={2.25} />Inspection report<em>Pre6 Lens</em></span>
							<span class="tile none"><X size={12} strokeWidth={2.5} />Wired into your workflow</span>
						</div>
						<p>Control plans and reports. Getting them into your workflow is on you.</p>
					{:else if r.id === "pace"}
						<div class="v pace" aria-hidden="true">
							<span class="run"><i></i><em>waits on people</em><i></i><em>waits on people</em><i></i></span>
						</div>
						<p>Fast at each task. The job waits on people between them.</p>
					{:else if r.id === "can"}
						<div class="v">
							<span class="tile">Pre6 Lens</span>
							<span class="tile">Pre6 Costing</span>
							<span class="tile">Pre6 Machinist</span>
							<span class="tile">Pre6 PPAP</span>
						</div>
						<p>One-off tasks, built the same for every customer.</p>
					{:else if r.id === "runs"}
						<div class="v"><span class="tile"><Cloud size={12} strokeWidth={2.25} />Pre6 Cloud</span></div>
						<p>Hosted by Pre6. One setup for every customer.</p>
					{:else}
						<div class="v"><span class="tile">Sign up and start</span></div>
						<p>Self-serve.</p>
					{/if}
				</div>

				<!-- Pre6 Enterprise -->
				<div class="ent" role="cell">
					<span class="m">Pre6 Enterprise</span>
					{#if r.id === "flow"}
						<div class="v chain">
							<span class="node" style:--k="0"><Mail size={12} strokeWidth={2.25} />Inbox<small>02:14</small></span>
							<span class="step" style:--k="1"
								><span class="to"></span><span class="node crew-n"
									><span class="mini">{#each Object.values(A) as a (a.name)}{@render dot(a, 16)}{/each}</span>Crew</span
								></span
							>
							<span class="step" style:--k="2"
								><span class="to"></span><span class="lands">{#each TOOLS as t (t.name)}<span class="tool" style:--c={t.color}><i></i>{t.name}</span>{/each}</span></span
							>
							<span class="step" style:--k="3"><span class="to"></span><span class="node you-n ok"><Check size={12} strokeWidth={3} />You approve</span></span>
						</div>
						<p>It starts where the ECN lands and ends inside your tools. Nobody carries it. You step in once, to approve.</p>
					{:else if r.id === "knows"}
						<div class="v know">
							<span class="srcs">
								{#each ["PLM archive", "ERP history", "QMS records", "Past emails"] as s, n (s)}<span class="src" style:--k={n}>{s}</span>{/each}
							</span>
							<span class="recall" style:--k="4">{@render dot(A.qa, 18)}<span><b>SK-1904, 2021.</b> Went to H6 and needed a hone op and 100% air gauging.</span></span>
						</div>
						<p>Decades of your drawings, quotes, ECNs and NCRs.</p>
					{:else if r.id === "who"}
						<ul class="v crewlist">
							{#each CREW as c, n (c.name)}
								<li style:--k={n}>{@render dot(c, 26)}<span class="who-t"><b>{c.name}</b><span class="task">{c.task}</span></span></li>
							{/each}
							<li class="add" style:--k={CREW.length}>
								<span class="dot more" style:--s="26px"><Plus size={14} strokeWidth={2.75} /></span><span class="who-t"><b>Any agent you need</b><span class="task">Added as the work grows</span></span>
							</li>
						</ul>
						<p>A crew, each on its part, checking each other.</p>
					{:else if r.id === "get"}
						<ul class="v ready">
							{#each DELIVERABLES as d, n (d)}
								<li style:--k={n}><span class="tick"><Check size={11} strokeWidth={3} /></span>{d}</li>
							{/each}
						</ul>
						<p>Finished work, wired into your workflow, ready to approve.</p>
					{:else if r.id === "pace"}
						<div class="v pace">
							<span class="run solid" style:--k="0"><i></i></span>
							<span class="cite" style:--k="1"><Check size={11} strokeWidth={3} />Checked against SK-1904, ECN-0431 and 2 past PPAPs</span>
						</div>
						<p>Fast end to end, every step checked against your history.</p>
					{:else if r.id === "can"}
						<div class="v swarm">
							<span class="tile mine" style:--k="0"><Network size={12} strokeWidth={2.5} />ECN impact crew<em>{SWARM.length} agents</em></span>
							<ul class="swarm-grid" aria-label="Agents in the crew">
								{#each SWARM as a, n (a.task)}
									<li class="bee" style:--c={a.color} style:--n={n} title={a.task} aria-label={a.task}>
										<a.icon size={14} strokeWidth={2.25} /><span class="ok" aria-hidden="true"><Check size={8} strokeWidth={4} /></span>
									</li>
								{/each}
								<!-- Room for more: extra agents join as the workload grows. -->
								{#each [0, 1, 2] as g (g)}
									<li class="bee more" style:--g={g} aria-hidden="true"><Plus size={13} strokeWidth={2.5} /></li>
								{/each}
							</ul>
							<span class="swarm-done" style:--k="2"><Check size={11} strokeWidth={3} />Done end to end, ready to approve</span>
						</div>
						<p>Tailor-made for any job. A crew takes it end to end and grows with the workload.</p>
					{:else if r.id === "runs"}
						<div class="v">
							<span class="tile" style:--k="0"><Cloud size={12} strokeWidth={2.25} />Pre6 Cloud</span>
							<span class="tile" style:--k="1"><Server size={12} strokeWidth={2.25} />Pre6 Edge</span>
							<span class="tile picked" style:--k="2"><Lock size={12} strokeWidth={2.5} />Air-gapped on-prem</span>
						</div>
						<p>Wherever your data has to stay.</p>
					{:else}
						<div class="v target" style:--k="0">
							<span class="t-k">Target set with you</span>
							<b>ECN impact out within 24 hours</b>
						</div>
						<p>Built with your team, measured on your targets.</p>
					{/if}
				</div>
			</div>
		{/each}
	</div>
</div>

<style>
	.cmp {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		--pop: cubic-bezier(0.34, 1.5, 0.64, 1);
		--panel: color-mix(in oklch, var(--primary) 4%, #fff);
		--ring: color-mix(in oklch, var(--primary) 32%, transparent);
		display: flex;
		flex-direction: column;
		gap: 22px;
		padding: 28px var(--gutter, 32px) 36px;
	}

	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
	}

	/* The job: one line of the ECN, so both columns answer the same thing. */
	.job {
		display: flex;
		align-items: center;
		gap: 16px;
		opacity: 0;
		transform: translateY(10px);
	}

	.job:global([data-seen]) {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.5s var(--ease),
			transform 0.6s var(--ease);
	}

	.job-k {
		flex: none;
		color: var(--muted-foreground);
		font: 600 11px/1 var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.job-mail {
		display: flex;
		flex: 1;
		align-items: center;
		gap: 12px;
		padding: 12px 16px;
		border-radius: 12px;
		background: #fff;
		box-shadow:
			inset 0 0 0 1px var(--border),
			0 10px 24px -18px rgb(15 23 42 / 0.35);
	}

	.job-ic {
		display: grid;
		flex: none;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--ink);
		color: #fff;
	}

	.job-mail p {
		color: var(--foreground);
		font-size: 14.5px;
		line-height: 1.5;
	}

	.job-mail b {
		color: var(--ink);
		font-weight: 600;
	}

	/* The table: a label column, the apps column, and the Enterprise column on one panel. */
	.grid {
		display: grid;
		grid-template-columns: 180px minmax(0, 1fr) minmax(0, 1.3fr);
	}

	.row {
		display: grid;
		grid-column: 1 / -1;
		grid-template-columns: subgrid;
		opacity: 0;
		transform: translateY(12px);
	}

	.row.head,
	.row:global([data-seen]) {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.5s var(--ease),
			transform 0.6s var(--ease);
	}

	.row + .row:not(.head) > .k,
	.row + .row:not(.head) > .apps {
		border-top: 1px solid var(--border);
	}

	.k {
		display: flex;
		align-items: flex-start;
		gap: 10px;
		padding: 20px 16px 20px 0;
		color: var(--ink);
		font-size: 14px;
		font-weight: 600;
		line-height: 1.35;
	}

	.k-ic {
		display: grid;
		flex: none;
		place-items: center;
		width: 26px;
		height: 26px;
		margin-top: -4px;
		border-radius: 7px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--border);
		color: var(--muted-foreground);
	}

	.apps,
	.ent {
		display: flex;
		flex-direction: column;
		gap: 10px;
		min-width: 0;
		padding: 18px 22px;
	}

	.apps p,
	.ent p {
		color: var(--muted-foreground);
		font-size: 13.5px;
		line-height: 1.45;
	}

	.ent p {
		color: var(--foreground);
		font-weight: 500;
	}

	/* The Enterprise panel: one tinted column with a red ring, built from its cells. */
	.ent {
		position: relative;
		background: var(--panel);
		box-shadow:
			inset 1.5px 0 0 var(--ring),
			inset -1.5px 0 0 var(--ring);
	}

	.row + .row:not(.head) > .ent::before {
		position: absolute;
		top: 0;
		right: 22px;
		left: 22px;
		border-top: 1px dashed var(--ring);
		content: "";
	}

	.head .ent {
		border-radius: 14px 14px 0 0;
		box-shadow:
			inset 1.5px 0 0 var(--ring),
			inset -1.5px 0 0 var(--ring),
			inset 0 1.5px 0 var(--ring);
	}

	.row:last-child .ent {
		border-radius: 0 0 14px 14px;
		box-shadow:
			inset 1.5px 0 0 var(--ring),
			inset -1.5px 0 0 var(--ring),
			inset 0 -1.5px 0 var(--ring),
			0 30px 50px -36px color-mix(in oklch, var(--primary) 70%, transparent);
	}

	.head .apps,
	.head .ent {
		justify-content: flex-end;
		gap: 4px;
		padding-block: 22px 18px;
	}

	.head b {
		color: var(--muted-foreground);
		font-size: 18px;
		font-weight: 700;
		letter-spacing: -0.01em;
	}

	.head .ent b {
		color: var(--ink);
		font-size: 22px;
	}

	.stop {
		color: var(--primary);
	}

	.head small {
		color: var(--muted-foreground);
		font: 500 11.5px/1.3 var(--font-mono);
		letter-spacing: 0.02em;
	}

	.crew {
		display: flex;
		margin-bottom: 8px;
	}

	.crew .dot + .dot {
		margin-left: -5px;
	}

	.crew .dot {
		box-shadow: 0 0 0 2px var(--panel);
	}

	/* The open seat at the end of the crew: room for the next agent. */
	.crew .dot.more {
		border: 1.5px dashed color-mix(in oklch, var(--primary) 55%, transparent);
		background: var(--panel);
		color: var(--primary);
	}

	/* The mobile labels for each cell, hidden while the header row does the job. */
	.m {
		display: none;
	}

	.dot {
		display: grid;
		flex: none;
		place-items: center;
		width: var(--s);
		height: var(--s);
		border-radius: 30%;
		background: var(--c);
		color: #fff;
	}

	.dot.grey {
		background: color-mix(in oklch, var(--muted-foreground) 55%, #fff);
	}

	/* Shared pieces. */
	.v {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		min-width: 0;
		font-size: 13px;
	}

	.file,
	.tile,
	.src,
	.tool {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 10px;
		border-radius: 7px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--border);
		color: var(--foreground);
		font-size: 12.5px;
		font-weight: 500;
		white-space: nowrap;
	}

	.file {
		font: 500 11.5px/1.3 var(--font-mono);
	}

	.apps .v {
		color: var(--muted-foreground);
	}

	/* Apps pieces: plain and grey. */
	.one {
		flex-wrap: nowrap;
		align-items: center;
		gap: 8px;
		color: var(--foreground);
	}

	.targets {
		display: flex;
		gap: 4px;
	}

	.targets span {
		padding: 4px 8px;
		border: 1.5px dashed color-mix(in oklch, var(--muted-foreground) 40%, transparent);
		border-radius: 7px;
		color: var(--muted-foreground);
		font-size: 12px;
	}

	.tile.none {
		box-shadow: none;
		outline: 1.5px dashed color-mix(in oklch, var(--muted-foreground) 40%, transparent);
		outline-offset: -1.5px;
		background: transparent;
		color: var(--muted-foreground);
		text-decoration: line-through;
	}

	/* Enterprise pieces: color, and each one lands in turn once the row is seen. */
	.ent .v > *,
	.ent .v.target {
		opacity: 0;
		translate: 0 6px;
	}

	.row:global([data-seen]) .ent .v > *,
	.row:global([data-seen]) .ent .v.target {
		opacity: 1;
		translate: none;
		transition:
			opacity 0.35s var(--ease) calc(0.25s + var(--k, 0) * 0.09s),
			translate 0.5s var(--pop) calc(0.25s + var(--k, 0) * 0.09s);
	}

	.crewlist li,
	.ready li {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
		color: var(--foreground);
	}

	.crewlist b {
		font-weight: 600;
	}

	.know {
		flex-direction: column;
		gap: 8px;
	}

	.srcs {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.src {
		background: color-mix(in oklch, var(--ink) 5%, #fff);
		font-size: 12px;
	}

	.srcs .src {
		opacity: 0;
		translate: 0 6px;
	}

	.row:global([data-seen]) .srcs .src {
		opacity: 1;
		translate: none;
		transition:
			opacity 0.35s var(--ease) calc(0.25s + var(--k) * 0.09s),
			translate 0.5s var(--pop) calc(0.25s + var(--k) * 0.09s);
	}

	.recall {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		padding: 9px 11px;
		border-radius: 9px;
		background: color-mix(in oklch, var(--agent-qa) 7%, #fff);
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--agent-qa) 35%, transparent);
		color: var(--foreground);
		line-height: 1.45;
	}

	.recall b {
		color: var(--ink);
	}

	/* The crew: a clear icon, the agent's name in ink, and its part of the job on the line under it. */
	.crewlist {
		flex-direction: column;
		gap: 10px;
	}

	.crewlist li {
		display: grid;
		grid-template-columns: 26px minmax(0, 1fr);
		align-items: center;
		gap: 10px;
		line-height: 1.35;
	}

	.who-t {
		display: flex;
		flex-direction: column;
		gap: 1px;
		min-width: 0;
	}

	.crewlist .who-t b {
		color: var(--ink);
		font-size: 14px;
	}

	.task {
		color: var(--foreground);
		font-size: 13.5px;
	}

	/* The open seat at the end of the crew list, dashed like the one in the header. */
	.dot.more {
		border: 1.5px dashed color-mix(in oklch, var(--primary) 55%, transparent);
		background: transparent;
		color: var(--primary);
	}

	.crewlist .add b {
		color: var(--primary);
	}

	.ready {
		gap: 6px 14px;
	}

	.ready li {
		font-weight: 500;
	}

	.tick {
		display: grid;
		flex: none;
		place-items: center;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: var(--ok);
		color: #fff;
	}

	.tool {
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--c) 35%, transparent);
		color: var(--ink);
	}

	.tool i {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--c);
	}

	.tile.mine {
		flex-wrap: wrap;
		gap: 6px 8px;
		white-space: normal;
		padding: 7px 12px;
		background: var(--primary);
		box-shadow: 0 8px 18px -10px var(--primary);
		color: #fff;
		font-weight: 600;
	}

	.tile.mine em {
		padding: 2px 7px;
		border-radius: 999px;
		background: rgb(255 255 255 / 0.18);
		font: 600 10.5px/1.3 var(--font-mono);
		font-style: normal;
	}

	.tile.picked {
		background: var(--ink);
		box-shadow: none;
		color: #fff;
	}

	.target {
		display: flex;
		flex-direction: column;
		gap: 4px;
		padding: 10px 14px;
		border-left: 3px solid var(--primary);
		border-radius: 4px 9px 9px 4px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--border);
	}

	.t-k {
		color: var(--muted-foreground);
		font: 600 10.5px/1 var(--font-mono);
		letter-spacing: 0.06em;
		text-transform: uppercase;
	}

	.target b {
		color: var(--ink);
		font-size: 14.5px;
		font-weight: 600;
	}

	/* How the work moves: a chain of stops. In the apps column a person is two of them. */
	.chain {
		align-items: center;
		gap: 6px;
	}

	.node {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 5px 10px;
		border-radius: 7px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--border);
		color: var(--ink);
		font-size: 12.5px;
		font-weight: 600;
		white-space: nowrap;
	}

	.node small {
		color: var(--muted-foreground);
		font: 600 11px/1 var(--font-mono);
	}

	.you-n {
		border-radius: 999px;
		background: color-mix(in oklch, var(--muted-foreground) 18%, #fff);
		box-shadow: none;
	}

	.you-n.ok {
		background: var(--ok);
		color: #fff;
	}

	.crew-n {
		box-shadow: inset 0 0 0 1px var(--ring);
	}

	.mini {
		display: inline-flex;
	}

	.mini .dot + .dot {
		margin-left: -4px;
	}

	.mini .dot {
		box-shadow: 0 0 0 1.5px #fff;
	}

	.step {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
	}

	.lands {
		display: inline-flex;
		flex-wrap: wrap;
		gap: 4px;
	}

	.lands .tool {
		padding: 4px 8px;
		font-size: 12px;
	}

	.to {
		position: relative;
		flex: none;
		width: 14px;
		height: 1.5px;
		background: color-mix(in oklch, var(--muted-foreground) 60%, transparent);
	}

	.to::after {
		position: absolute;
		top: 50%;
		right: 0;
		width: 6px;
		height: 6px;
		border-top: 1.5px solid color-mix(in oklch, var(--muted-foreground) 60%, transparent);
		border-right: 1.5px solid color-mix(in oklch, var(--muted-foreground) 60%, transparent);
		content: "";
		translate: 0 -50%;
		rotate: 45deg;
	}

	.ent .to {
		background: var(--ring);
	}

	.ent .to::after {
		border-color: var(--ring);
	}

	/* Column speed tags: first flight for the apps, space travel for Enterprise. */
	.speed-tag {
		display: inline-flex;
		align-items: center;
		align-self: flex-start;
		gap: 6px;
		margin-bottom: 6px;
		color: var(--muted-foreground);
		font: 600 10.5px/1 var(--font-mono);
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.speed-tag.space {
		color: var(--primary);
	}

	.apps-set {
		display: inline-flex;
		flex: 0 1 auto;
		flex-wrap: wrap;
		gap: 4px;
		min-width: 0;
	}

	.file em {
		padding: 1px 5px;
		border-radius: 4px;
		background: color-mix(in oklch, var(--ink) 7%, #fff);
		color: var(--ink);
		font: 600 10px/1.3 var(--font-mono);
		font-style: normal;
	}

	/* How fast, how sure: the apps run in short bursts with waits between; Enterprise runs straight through. */
	.pace {
		flex-direction: column;
		gap: 8px;
	}

	.run {
		display: flex;
		align-items: center;
		gap: 6px;
		width: 100%;
	}

	.run i {
		flex: 1;
		height: 6px;
		border-radius: 999px;
		background: color-mix(in oklch, var(--muted-foreground) 55%, #fff);
	}

	.run em {
		flex: 1.6;
		padding-top: 1px;
		border-top: 2px dashed color-mix(in oklch, var(--muted-foreground) 35%, transparent);
		color: var(--muted-foreground);
		font: italic 500 10.5px/1.6 var(--font-sans);
		text-align: center;
	}

	/* Solid Pre6 red, with a white streak that keeps running along it. */
	.run.solid i {
		position: relative;
		overflow: hidden;
		height: 6px;
		background: var(--primary);
	}

	.run.solid i::after {
		position: absolute;
		inset: 0 auto 0 0;
		width: 30%;
		background: linear-gradient(90deg, transparent, #fff 70%, transparent);
		content: "";
		opacity: 0;
	}

	.row:global([data-seen]) .run.solid i::after {
		animation: streak 2.4s cubic-bezier(0.65, 0, 0.35, 1) 0.8s infinite;
	}

	@keyframes streak {
		0% {
			opacity: 0.9;
			translate: -100% 0;
		}
		55% {
			opacity: 0.9;
			translate: 340% 0;
		}
		56%,
		100% {
			opacity: 0;
			translate: 340% 0;
		}
	}

	.cite {
		display: inline-flex;
		align-items: center;
		align-self: flex-start;
		gap: 5px;
		padding: 3px 9px 3px 6px;
		border-radius: 999px;
		background: color-mix(in oklch, var(--ok) 10%, #fff);
		color: color-mix(in oklch, var(--ok) 85%, var(--ink));
		font: 600 11.5px/1.3 var(--font-sans);
	}

	/* What it can do: a crew deployed on the ECN, each agent on one piece, finishing it end to end. */
	.swarm {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}

	.swarm-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	/* Each agent in the crew: its icon on its color, with a small check once its piece is done. */
	.bee {
		position: relative;
		display: grid;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--c);
		box-shadow: 0 6px 12px -8px var(--c);
		color: #fff;
		opacity: 0;
		scale: 0.6;
	}

	.bee .ok {
		position: absolute;
		right: -4px;
		bottom: -4px;
		display: grid;
		place-items: center;
		width: 13px;
		height: 13px;
		border: 1.5px solid var(--panel);
		border-radius: 50%;
		background: var(--ok);
		color: #fff;
	}

	/* The open seats: dashed, breathing in turn, for the agents that join as the work grows. */
	.bee.more {
		border: 1.5px dashed color-mix(in oklch, var(--primary) 50%, transparent);
		background: transparent;
		box-shadow: none;
		color: var(--primary);
	}

	/* The crew fans out one agent at a time, each popping in with a small overshoot. */
	.row:global([data-seen]) .bee {
		opacity: 1;
		scale: 1;
		transition:
			opacity 0.25s var(--ease) calc(0.45s + var(--n, 7) * 0.07s),
			scale 0.45s var(--pop) calc(0.45s + var(--n, 7) * 0.07s);
	}

	.row:global([data-seen]) .bee.more {
		animation: room 2.4s ease-in-out calc(1.3s + var(--g) * 0.3s) infinite;
	}

	@keyframes room {
		0%,
		100% {
			opacity: 0.35;
			scale: 0.92;
		}
		50% {
			opacity: 1;
			scale: 1;
		}
	}

	.swarm-done {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 3px 9px 3px 6px;
		border-radius: 999px;
		background: color-mix(in oklch, var(--ok) 10%, #fff);
		color: color-mix(in oklch, var(--ok) 85%, var(--ink));
		font: 600 11.5px/1.3 var(--font-sans);
	}

	.row:global([data-seen]) .swarm .swarm-done {
		transition-delay: calc(0.5s + 7 * 0.07s + 0.2s);
	}

	/* Tablet: the label rides above each pair of cells. */
	@media (max-width: 1023px) {
		.grid {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
		}

		/* The label sits over the apps answer; the Enterprise cell spans both, so its panel stays whole. */
		.k {
			grid-row: 1;
			grid-column: 1;
			padding: 18px 16px 4px 0;
		}

		.head .k {
			display: none;
		}

		.apps {
			grid-row: 2;
			grid-column: 1;
			padding-left: 0;
		}

		.ent {
			grid-row: 1 / span 2;
			grid-column: 2;
		}

		.head .apps,
		.head .ent {
			grid-row: 1;
		}

		.row + .row:not(.head) > .apps {
			border-top: 0;
		}
	}

	/* Phone: the two answers stack under the label, each marked with its column's name. */
	@media (max-width: 767px) {
		.cmp {
			padding-inline: 20px;
		}

		.job {
			flex-direction: column;
			align-items: flex-start;
			gap: 10px;
		}

		.grid {
			grid-template-columns: minmax(0, 1fr);
		}

		.head {
			display: none;
		}

		.m {
			display: block;
			color: var(--muted-foreground);
			font: 600 10.5px/1 var(--font-mono);
			letter-spacing: 0.08em;
			text-transform: uppercase;
		}

		.ent .m {
			color: var(--primary);
		}

		.apps {
			padding: 4px 0 14px;
		}

		.ent,
		.row:last-child .ent {
			border-radius: 12px;
			box-shadow: inset 0 0 0 1.5px var(--ring);
		}

		.row + .row:not(.head) > .ent::before {
			display: none;
		}

		.k,
		.apps,
		.ent {
			grid-row: auto;
			grid-column: auto;
		}

		.k {
			padding: 20px 0 8px;
		}

	}

	@media (prefers-reduced-motion: reduce) {
		.bee {
			opacity: 1;
			scale: 1;
			transition: none !important;
			animation: none !important;
		}

		.run.solid i::after {
			display: none;
		}

		.job,
		.row,
		.ent .v > *,
		.ent .v.target,
		.srcs .src {
			opacity: 1;
			transform: none;
			translate: none;
			transition: none !important;
		}
	}
</style>
